import { lazy, Suspense } from "react";
import { Switch, Route, Redirect, useLocation, useSearch, Router as WouterRouter } from "wouter";
import BetaBanner, { BETA_MODE, BANNER_HEIGHT_PX } from "@/components/BetaBanner";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/hooks/useTheme";
import { UpgradeModalProvider } from "@/components/UpgradeModal";
import { LanguageProvider, useLanguage } from "@/hooks/useLanguage";
import { useAuth } from "@/hooks/useAuth";
import SettingsToggle from "@/components/SettingsToggle";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AstroAssistant from "@/components/AstroAssistant";
import GlobalFeedback from "@/components/GlobalFeedback";
import NotificationBell from "@/components/NotificationBell";
import BottomNav from "@/components/BottomNav";
import AppSidebar, { SidebarCollapseProvider, useSidebarCollapsed } from "@/components/AppSidebar";
import AutoTranslate from "@/components/AutoTranslate";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import { NavHistoryTracker } from "@/components/BackButton";
import { NO_APP_CHROME_ROUTES } from "@/lib/appChrome";
import { compatibilityRedirectFromSearch } from "@/lib/compatibilityPayload";
import EmailVerificationRequired from "@/components/EmailVerificationRequired";
import { useSeoRouteGuard } from "@/lib/seo";
import {
  KundaliRouteLoadingState,
  KundaliRouteRecovery,
} from "@/components/KundaliRouteRecovery";
import { AppRecoveryBoundary } from "@/components/AppRecoveryBoundary";
import AdminPageGate from "@/components/AdminPageGate";

const BirthDateOnboarding = lazy(() => import("@/pages/BirthDateOnboarding"));
const Home = lazy(() => import("@/pages/Home"));
const Landing = lazy(() => import("@/pages/Landing"));
const WellnessTips = lazy(() => import("@/pages/WellnessTips"));
const Compatibility = lazy(() => import("@/pages/Compatibility"));
const CompatibilityHub = lazy(() => import("@/pages/CompatibilityHub"));
const TeamAnalysis = lazy(() => import("./pages/TeamAnalysis"));
const MarriageCompatibility = lazy(() => import("./pages/MarriageCompatibility"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const ZodiacSignPage = lazy(() => import("@/pages/ZodiacSign"));
const SharedReport = lazy(() => import("@/pages/SharedReport"));
const RelationshipJournal = lazy(() => import("@/pages/RelationshipJournal"));
const FamilyCompatibility = lazy(() => import("@/pages/FamilyCompatibility"));
const RelationshipCoach = lazy(() => import("@/pages/RelationshipCoach"));
const AiAstrologer = lazy(() => import("@/pages/AiAstrologer"));
const ImprovementTracker = lazy(() => import("@/pages/ImprovementTracker"));
const DailyProgress = lazy(() => import("@/pages/DailyProgress"));
const MLValidation = lazy(() => import("@/pages/MLValidation"));
const TarotReading = lazy(() => import("@/pages/TarotReading"));
const PalmReading = lazy(() => import("@/pages/PalmReading"));
const AiAvatar = lazy(() => import("@/pages/AiAvatar"));
const CosmicChallenges = lazy(() => import("@/pages/CosmicChallenges"));
const ClassroomHarmony = lazy(() => import("@/pages/ClassroomHarmony"));
const AshtakootBirth = lazy(() => import("@/pages/AshtakootBirth"));
const MatchPage = lazy(() => import("@/pages/Match"));
const KundaliPage = lazy(() => import("@/pages/Kundali"));
const GuidancePage = lazy(() => import("@/pages/Guidance"));
const MuhuratPage = lazy(() => import("@/pages/Muhurat"));
const PanchangPage = lazy(() => import("@/pages/Panchang"));
const FestivalsPage = lazy(() => import("@/pages/Festivals"));
const JyotishTiersPage = lazy(() => import("@/pages/JyotishTiers"));
const Numerology = lazy(() => import("@/pages/Numerology"));
const Gemstones = lazy(() => import("@/pages/Gemstones"));
const Blog = lazy(() => import("@/pages/Blog"));
const DailyHoroscope = lazy(() => import("@/pages/DailyHoroscope"));
const CosmicScience = lazy(() => import("@/pages/CosmicScience"));
const CosmicNutrition = lazy(() => import("@/pages/CosmicNutrition"));
const Profile = lazy(() => import("@/pages/Profile"));
const About = lazy(() => import("@/pages/About"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Refund = lazy(() => import("@/pages/Refund"));
const Contact = lazy(() => import("@/pages/Contact"));
const AiUsage = lazy(() => import("@/pages/AiUsage"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const NotFound = lazy(() => import("@/pages/not-found"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Session = lazy(() => import("@/pages/Session"));
const SessionRate = lazy(() => import("@/pages/SessionRate"));
const Bookings = lazy(() => import("@/pages/Bookings"));
const EmbedHub = lazy(() => import("@/pages/EmbedHub"));
const BacklinkHub = lazy(() => import("@/pages/BacklinkHub"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const SignHub = lazy(() => import("@/pages/SignHub"));
const CityHub = lazy(() => import("@/pages/CityHub"));
const AstrologerProfile = lazy(() => import("@/pages/AstrologerProfile"));
const AstrologerDashboard = lazy(() => import("@/pages/AstrologerDashboard"));
const TeamAdmin = lazy(() => import("@/pages/TeamAdmin"));
const TeamInvite = lazy(() => import("@/pages/TeamInvite"));
const SeoLanding = lazy(() => import("@/pages/SeoLanding"));
const CompatibilityLanding = lazy(() => import("@/pages/CompatibilityLanding"));
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const NotificationsCenter = lazy(() => import("@/pages/NotificationsCenter"));
const NotificationDetail = lazy(() => import("@/pages/NotificationDetail"));

// Help is code-split so it doesn't weigh down the initial bundle, but we
// prefetch it during idle time so the first click opens instantly (no
// network/transform wait at navigation time).
const importHelp = () => import("@/pages/Help");
const Help = lazy(importHelp);
const importAstroLearn = () => import("@/pages/AstroLearn");
const AstroLearn = lazy(importAstroLearn);
if (typeof window !== "undefined") {
  const idle: (cb: () => void) => void =
    (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ??
    ((cb) => setTimeout(cb, 1500));
  idle(() => {
    importHelp().catch(() => {});
    importAstroLearn().catch(() => {});
  });
}

const NO_SIDEBAR_ROUTES = NO_APP_CHROME_ROUTES;
const NO_SIDEBAR_PREFIXES = ["/zodiac/", "/report/"];

export function KundaliRoute() {
  return (
    <KundaliRouteRecovery onRetry={() => window.location.reload()}>
      <Suspense fallback={<KundaliRouteLoadingState />}>
        <KundaliPage />
      </Suspense>
    </KundaliRouteRecovery>
  );
}

export function AdminDashboardRoute() {
  return <AdminPageGate><AdminDashboard /></AdminPageGate>;
}

function Router() {
  const [location] = useLocation();
  const pathname = location.split(/[?#]/, 1)[0];
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4" aria-hidden="true"></i>
          <p className="text-muted-foreground">Loading your cosmic profile...</p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen cosmic-bg flex items-center justify-center px-4" role="status" aria-live="polite">
        <p className="text-muted-foreground">Loading page…</p>
      </div>
    }>
      <Switch>
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/verify-email" component={VerifyEmail} />
      <Route path="/team-invite" component={TeamInvite} />
      <Route path="/zodiac/:name" component={ZodiacSignPage} />
      <Route path="/report/:id" component={SharedReport} />
      <Route path="/" component={isAuthenticated ? Home : Landing} />
      <Route path="/home" component={isAuthenticated ? Home : Landing} />
      <Route path="/landing" component={Landing} />
      <Route path="/compatibility-hub" component={CompatibilityHub} />
      <Route path="/compatibility" component={CompatibilityRoute} />
      <Route path="/team">{() => <TeamAnalysis />}</Route>
      <Route path="/marriage" component={MarriageCompatibility} />
      <Route path="/pricing" component={Pricing} />
      {/* Birth Chart was merged into Kundali (Aug 2026) — redirect old links */}
      <Route path="/birth-chart">
        <Redirect to="/kundali" replace />
      </Route>
      <Route path="/wellness" component={WellnessTips} />
      <Route path="/relationship-journal" component={RelationshipJournal} />
      <Route path="/family" component={FamilyCompatibility} />
      <Route path="/relationship-coach" component={RelationshipCoach} />
      <Route path="/ai-astrologer" component={AiAstrologer} />
      <Route path="/daily-growth" component={DailyProgress} />
      <Route path="/improvement-tracker" component={ImprovementTracker} />
      <Route path="/daily-progress" component={DailyProgress} />
      <Route path="/ml-validation" component={MLValidation} />
      <Route path="/tarot" component={TarotReading} />
      <Route path="/palm-reading" component={PalmReading} />
      <Route path="/ai-avatar" component={AiAvatar} />
      <Route path="/cosmic-challenges" component={CosmicChallenges} />
      <Route path="/classroom">{() => <ClassroomHarmony />}</Route>
      <Route path="/match/shared/:token">{() => <MatchPage />}</Route>
      <Route path="/match">
        <Redirect to="/marriage?mode=kundali" replace />
      </Route>
      <Route path="/kundali-match">
        <Redirect to="/marriage?mode=kundali" replace />
      </Route>
      <Route path="/ashtakoot" component={AshtakootBirth} />
      <Route path="/compatibility/birth" component={AshtakootBirth} />
       <Route path="/kundali/result" component={KundaliRoute} />
       <Route path="/kundali" component={KundaliRoute} />
      <Route path="/guidance" component={GuidancePage} />
      <Route path="/muhurat" component={MuhuratPage} />
      <Route path="/panchang" component={PanchangPage} />
      <Route path="/festivals" component={FestivalsPage} />
      <Route path="/jyotish-tiers" component={JyotishTiersPage} />
      <Route path="/numerology" component={Numerology} />
      <Route path="/gemstones" component={Gemstones} />
      <Route path="/daily-horoscope" component={DailyHoroscope} />
      <Route path="/cosmic-science" component={CosmicScience} />
      <Route path="/cosmic-nutrition" component={CosmicNutrition} />
      <Route path="/profile" component={Profile} />
      <Route path="/birth-details">
        <Redirect to="/kundali" replace />
      </Route>
      <Route path="/onboarding" component={BirthDateOnboarding} />
      <Route path="/matches">
        <Redirect to="/" replace />
      </Route>
      <Route path="/help">
        <Suspense fallback={<PageLoadingState />}>
          <Help />
        </Suspense>
      </Route>
      <Route path="/astro-learn">
        <Suspense fallback={<PageLoadingState />}>
          <AstroLearn />
        </Suspense>
      </Route>
      <Route path="/about" component={About} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/refund" component={Refund} />
      <Route path="/contact" component={Contact} />
      <Route path="/ai-usage" component={AiUsage} />
      <Route path="/disclaimer" component={Disclaimer} />
      <Route path="/notifications" component={NotificationsCenter} />
      <Route path="/notifications/:id" component={NotificationDetail} />
      <Route path="/marketplace" component={Marketplace} />
      <Route path="/session/:bookingId/rate" component={SessionRate} />
      <Route path="/session/:bookingId" component={Session} />
      <Route path="/bookings" component={Bookings} />
      <Route path="/astrologer/:slug" component={AstrologerProfile} />
      <Route path="/astrologer-dashboard" component={AstrologerDashboard} />
      {/* Aliases (kept for inbound links + marketing CTA) */}
      <Route path="/astrologer/dashboard" component={AstrologerDashboard} />
      <Route path="/become-astrologer" component={AstrologerDashboard} />
      <Route path="/team-admin" component={TeamAdmin} />
      <Route path="/astrology/:sign/:topic" component={SeoLanding} />
      <Route path="/astrology/:sign" component={SignHub} />
      <Route path="/compatibility/:sign1/:sign2" component={CompatibilityLanding} />
      <Route path="/astrologer-in/:city/:service" component={CityLanding} />
      <Route path="/astrologer-in/:city" component={CityHub} />
      <Route path="/blog" component={Blog} />
      <Route path="/embed" component={EmbedHub} />
      <Route path="/admin/backlinks" component={BacklinkHub} />
      <Route path="/admin" component={AdminDashboardRoute} />
      <Route path="/admin/:section" component={AdminDashboardRoute} />
      <Route path="/:rest*" component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function CompatibilityRoute() {
  const search = useSearch();
  const redirectTo = compatibilityRedirectFromSearch(search);
  return redirectTo ? (
    <Redirect to={redirectTo} replace />
  ) : (
    <Compatibility />
  );
}

function AppWithLayout() {
  const [location] = useLocation();
  useSeoRouteGuard(location);
  const pathname = location.split(/[?#]/, 1)[0];
  const { user, isAuthenticated } = useAuth();
  const collapsed = useSidebarCollapsed();
  const verificationRequired = isAuthenticated && !user?.emailVerifiedAt;
  const viewingVerificationLink =
    pathname === "/verify-email" &&
    new URLSearchParams(window.location.search).has("token");
  const showSidebar =
    isAuthenticated &&
    !verificationRequired &&
    !NO_SIDEBAR_ROUTES.includes(pathname) &&
    !NO_SIDEBAR_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  const contentClass = !showSidebar
    ? "w-full"
    : collapsed
      ? "w-full min-w-0 md:ml-16 md:w-[calc(100%-4rem)] transition-[margin,width] duration-300"
      : "w-full min-w-0 md:ml-56 md:w-[calc(100%-14rem)] transition-[margin,width] duration-300";

  return (
    <>
      <Toaster />
      {verificationRequired && !viewingVerificationLink ? (
        <EmailVerificationRequired />
      ) : (
        <>
          <BetaBanner />
          <NavHistoryTracker />
          {showSidebar && <AppSidebar />}
          <div
            className={`${contentClass} box-border px-4 sm:px-6`}
            data-testid="app-content-shell"
          >
            {isAuthenticated && user?.emailVerifiedAt && (
              <div
                className="sticky z-30 -mx-4 border-b border-border/50 bg-background/95 px-4 backdrop-blur sm:-mx-6 sm:px-6"
                style={{ top: BETA_MODE ? BANNER_HEIGHT_PX : 0 }}
                data-testid="app-feedback-toolbar"
              >
                <div className="flex min-h-14 items-center justify-end gap-2">
                  <div
                    role="group"
                    aria-label="Preferences and notifications"
                    data-testid="app-top-right-controls"
                    className="flex h-10 items-center gap-1"
                  >
                    <SettingsToggle />
                    <NotificationBell />
                  </div>
                  <GlobalFeedback />
                </div>
              </div>
            )}
            <Router />
          </div>
          <BottomNav />
          {isAuthenticated && <AstroAssistant />}
          <InstallPwaPrompt />
        </>
      )}
    </>
  );
}

function TranslationLoadingOverlay() {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Applying language"
      data-no-translate
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/95 backdrop-blur-sm"
    >
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-2xl font-bold text-primary">
          ZA
        </div>
        <i className="fas fa-spinner fa-spin text-2xl text-primary" aria-hidden="true" />
        <p role="status" aria-live="polite" className="mt-3 font-medium text-foreground">
          Applying your language…
        </p>
      </div>
    </div>
  );
}

function TranslationSwitchBoundary({ children }: { children: React.ReactNode }) {
  const { isLanguageSwitching } = useLanguage();
  return (
    <>
      <div
        aria-hidden={isLanguageSwitching || undefined}
        inert={isLanguageSwitching || undefined}
      >
        {children}
      </div>
      {isLanguageSwitching && <TranslationLoadingOverlay />}
    </>
  );
}

export function PageLoadingState() {
  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center px-4" role="status" aria-live="polite">
      <p className="text-muted-foreground">Loading page…</p>
    </div>
  );
}

function App() {
  return (
    <AppRecoveryBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <AutoTranslate />
            <TranslationSwitchBoundary>
              <TooltipProvider>
                <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                  <UpgradeModalProvider>
                    <SidebarCollapseProvider>
                      <AppWithLayout />
                    </SidebarCollapseProvider>
                  </UpgradeModalProvider>
                </WouterRouter>
              </TooltipProvider>
            </TranslationSwitchBoundary>
          </LanguageProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </AppRecoveryBoundary>
  );
}

export default App;
